import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import _omit from 'lodash/omit';
import _get from 'lodash/get';
import _mapValues from 'lodash/mapValues';
import { useSelector, useDispatch, useStore } from 'react-redux';

import { getMachine } from 'lib/selectors/getMachine';
import { CREATE_ANNOTATION, UPDATE_ANNOTATION } from 'lib/api/mutations';
import { toISO, now } from 'lib/utils/date';
import {
  actionSubmitAnnotation,
  actionSubmitAnnotationFailure,
  actionSubmitAnnotationSuccess,
} from 'lib/actions';
import { throwErrorToast, throwSuccessToast } from 'lib/utils/toast';
import { useTranslation } from 'react-i18next';
import {
  getAnnotations,
  getAreAnnotationsLoading,
} from 'lib/selectors/getAnnotations';
import { getBugsnagUserInfo } from 'lib/selectors/getBugsnagUserInfo';
import { getDowntimeIntervals } from 'lib/selectors/getDowntimeIntervals';
import { getUnavailableStartAt } from 'lib/selectors/getUnavailableStartAt';
import { getIsInitialDataSet } from 'lib/selectors/getIsInitialDataSet';
import { bugsnag } from 'lib/external/bugsnag';

const initialState = {
  downtimes: {},
  annotationType: null,
  planned: false,
  plannedEnabled: true,
  note: '',
  splitting: '',
  splitTimestamp: null,
};

const plannedBehaviorToRadioValue = (value) => {
  if (value === 'operator' || value === 'unplanned') return false;
  return true;
};

const isPlannedEnabled = (annotationType) => {
  return annotationType.plannedBehavior === 'operator';
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET_ANNOTATION_TYPE':
      return {
        ...state,
        annotationType: action.annotationType,
        plannedEnabled: isPlannedEnabled(action.annotationType),
        planned: plannedBehaviorToRadioValue(
          action.annotationType.plannedBehavior
        ),
      };
    case 'UNSET_ANNOTATION_TYPE':
      return {
        ...state,
        annotationType: null,
        plannedEnabled: true,
        planned: false,
      };
    case 'SET_NOTE':
      return { ...state, note: action.note };
    case 'SET_PLANNED':
      return { ...state, planned: action.planned };
    case 'TOGGLE_DOWNTIME':
      if (state.downtimes[action.downtime.start]) {
        return {
          ...state,
          downtimes: _omit(state.downtimes, action.downtime.start),
          note: '',
          splitting: false,
          splitTimestamp: null,
        };
      }
      return {
        ...state,
        downtimes: {
          ...state.downtimes,
          [action.downtime.start]: action.downtime,
        },
        note: action.downtime.message,
        splitting: false,
        splitTimestamp: null,
      };

    case 'SELECT_DOWNTIME_TO_SPLIT':
      return {
        ...state,
        downtimes: {
          [action.downtime.start]: action.downtime,
        },
        note: '',
        splitting: true,
        splitTimestamp: action.splitTimestamp,
      };
    case 'CLEAR_DOWNTIMES':
      return {
        ...state,
        downtimes: {},
        note: '',
        splitting: false,
        splitTimestamp: null,
      };
    default:
      throw new Error();
  }
}

const formatDowntimesForRequest = (state, machine) => {
  const {
    annotationType,
    downtimes,
    note,
    planned,
    splitting,
    splitTimestamp,
  } = state;

  const categorized = _get(Object.values(downtimes), [0, 'categorized'], false);

  const payload = Object.values(downtimes).map((downtime) => {
    return {
      id: downtime.id || null,
      category_id: annotationType.id,
      end: downtime.end || null,
      machineId: machine.id,
      message: note,
      planned,
      start: splitting ? splitTimestamp || toISO(now()) : downtime.start,
    };
  });

  return [payload, categorized, splitting];
};

// Beyond this, submit checks against whatever history it has.
const RESYNC_WAIT_MS = 5000;

const toMs = (time) => {
  return new Date(time).getTime();
};

// Refreshed machine history can remove an uncategorized downtime that was
// selected from stale data. A real one only grows (it may still be open, or
// reopen once a stale heartbeat recovers); a phantom vanishes or shrinks.
const hasStaleSelection = (downtimes, intervals) => {
  return Object.values(downtimes).some((downtime) => {
    if (downtime.categorized) {
      return false;
    }
    return !intervals.some((interval) => {
      return (
        !interval.categorized &&
        toMs(interval.start) === toMs(downtime.start) &&
        (!downtime.end ||
          !interval.end ||
          toMs(interval.end) >= toMs(downtime.end))
      );
    });
  });
};

// An open selection whose downtime has since ended takes the refreshed end, so
// the annotation doesn't also cover the running time after it. A stale
// heartbeat also ends the open downtime, at its last event, but the machine
// hasn't run since then.
const withRefreshedEnds = (downtimes, intervals, unavailableStartAt) => {
  return _mapValues(downtimes, (downtime) => {
    if (downtime.categorized || downtime.end) {
      return downtime;
    }
    const refreshed = intervals.find((interval) => {
      return (
        !interval.categorized && toMs(interval.start) === toMs(downtime.start)
      );
    });
    if (
      !refreshed?.end ||
      (unavailableStartAt && toMs(refreshed.end) === toMs(unavailableStartAt))
    ) {
      return downtime;
    }
    return { ...downtime, end: refreshed.end };
  });
};

// Categorize renders under the loading screen, so a preselection can arrive
// before the history it's checked against. Downtime is split around
// annotations, which load separately, so both have to be in.
const isHistoryLoaded = (state) => {
  return getIsInitialDataSet(state) && !getAreAnnotationsLoading(state);
};

const waitAtMost = (promise, ms) => {
  let timeout;
  const deadline = new Promise((resolve) => {
    timeout = setTimeout(resolve, ms);
  });
  return Promise.race([promise, deadline]).then(() => {
    clearTimeout(timeout);
  });
};

const useCategorization = ({ returnPath, waitForResync }) => {
  const globalDispatch = useDispatch();
  const history = useHistory();
  const machine = useSelector(getMachine);
  const [state, dispatch] = useReducer(reducer, initialState);
  const { t } = useTranslation();
  const annotations = useSelector(getAnnotations);
  const annotationsLoading = useSelector(getAreAnnotationsLoading);
  const bugsnagUserInfo = useSelector(getBugsnagUserInfo);
  const store = useStore();
  const downtimeIntervals = useSelector(getDowntimeIntervals);
  const historyLoaded = useSelector(isHistoryLoaded);
  const [selectionResets, setSelectionResets] = useState(0);
  const resetForIntervalsRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // A saved annotation categorizes the selection, so stop checking once sent.
  const submittingRef = useRef(false);
  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const resetStaleSelection = useCallback(
    (intervals) => {
      // Submit and the effect below can both catch the same refresh.
      if (resetForIntervalsRef.current === intervals) {
        return;
      }
      resetForIntervalsRef.current = intervals;
      dispatch({ type: 'CLEAR_DOWNTIMES' });
      setSelectionResets((count) => {
        return count + 1;
      });
      throwErrorToast(
        t(
          'This downtime changed after the latest machine data loaded. Please select it again.'
        )
      );
    },
    [t]
  );

  useEffect(() => {
    if (
      historyLoaded &&
      !submittingRef.current &&
      hasStaleSelection(state.downtimes, downtimeIntervals)
    ) {
      resetStaleSelection(downtimeIntervals);
    }
  }, [historyLoaded, downtimeIntervals, state.downtimes, resetStaleSelection]);

  const displayErrors = {
    'Annotations start must be before they end.': t(
      'A downtime may already be categorized during this time.'
    ),
  };

  const onCompleted = useCallback(
    ({ updateAnnotationResponse, createAnnotationResponse }) => {
      const response = updateAnnotationResponse || createAnnotationResponse;
      if (response) {
        const newAnnotations = Array.isArray(response) ? response : [response];
        throwSuccessToast(t('Downtime categorized successfully'));
        globalDispatch(actionSubmitAnnotationSuccess(newAnnotations));
      } else {
        // null response, caused by 404 if annotation type or annotation does not exist
        throwErrorToast(
          `${t('Could not categorize downtime')}. ${t(
            'Downtime or downtime category not found'
          )}.`
        );

        bugsnag.notify(
          new Error('Error categorizing downtime'),
          (event) => {
            event.addMetadata('info', {
              error: 'No response on completed downtime request',
              annotations: state.downtimes,
            });
            event.addMetadata('user', bugsnagUserInfo);
          },
          () => {}
        );

        globalDispatch(actionSubmitAnnotationFailure());
      }
    },
    [t, globalDispatch, bugsnagUserInfo, state]
  );

  const onError = useCallback(
    ({ networkError }) => {
      if (networkError) {
        const [payload, categorized, splitting] = formatDowntimesForRequest(
          state,
          machine
        );
        const errorMsg = _get(
          networkError,
          ['result', 'errors', 0, 'message'],
          ''
        );
        const displayMsg = displayErrors[errorMsg] || errorMsg;
        throwErrorToast(
          `${t('Could not categorize downtime')}${displayMsg ? ': ' : '.'}${t(
            displayMsg
          )}`
        );

        bugsnag.notify(
          new Error('Error categorizing downtime'),
          (event) => {
            event.addMetadata('info', {
              annotationHeaders: payload,
              previouslyCategorized: categorized,
              splitting,
              errorMsg,
            });
            event.addMetadata('user', bugsnagUserInfo);
          },
          () => {}
        );
      }
      globalDispatch(actionSubmitAnnotationFailure());
    },
    [t, displayErrors, state, machine, globalDispatch, bugsnagUserInfo]
  );

  const [createAnnotation, { error }] = useMutation(CREATE_ANNOTATION, {
    fetchPolicy: 'no-cache',
    onCompleted,
    onError,
  });

  const [updateAnnotation, { error: updateError }] = useMutation(
    UPDATE_ANNOTATION,
    {
      fetchPolicy: 'no-cache',
      onCompleted,
      onError,
    }
  );

  const submit = useCallback(async () => {
    if (submittingRef.current || !Object.keys(state.downtimes).length) {
      return;
    }
    submittingRef.current = true;
    setIsSubmitting(true);
    if (waitForResync) {
      await waitAtMost(waitForResync(), RESYNC_WAIT_MS);
    }
    if (!isMountedRef.current) {
      return;
    }
    const latestState = store.getState();
    const latestIntervals = getDowntimeIntervals(latestState);
    if (
      isHistoryLoaded(latestState) &&
      hasStaleSelection(state.downtimes, latestIntervals)
    ) {
      submittingRef.current = false;
      setIsSubmitting(false);
      resetStaleSelection(latestIntervals);
      return;
    }

    const [downtimesArray, categorized, splitting] = formatDowntimesForRequest(
      {
        ...state,
        downtimes: withRefreshedEnds(
          state.downtimes,
          latestIntervals,
          getUnavailableStartAt(latestState)
        ),
      },
      machine
    );

    const payload =
      downtimesArray.length < 2 ? downtimesArray[0] : downtimesArray;

    globalDispatch(actionSubmitAnnotation());

    if (!categorized || splitting) {
      await createAnnotation({ variables: { input: payload } });
    } else if (categorized) {
      const upToDateAnno =
        annotations.find((anno) => {
          return anno.id === payload.id;
        }) || {};
      const end = upToDateAnno.isEndSet ? upToDateAnno.end : null;
      await updateAnnotation({ variables: { input: { ...payload, end } } });
    }

    history.push(returnPath);
  }, [
    createAnnotation,
    updateAnnotation,
    machine,
    state,
    annotations,
    globalDispatch,
    history,
    returnPath,
    waitForResync,
    store,
    resetStaleSelection,
  ]);

  return {
    error: error || updateError,
    loading: annotationsLoading,
    data: { ...state, dispatch, submit, selectionResets, isSubmitting },
  };
};

export {
  useCategorization,
  reducer,
  formatDowntimesForRequest,
  hasStaleSelection,
  withRefreshedEnds,
  initialState,
};
